/**
 * Network orchestration: load the appview map, scan a target account, and
 * write/remove follow records in the signed-in account's own repo.
 */
import {
  authorityOf,
  buildFollowRecord,
  buildIndex,
  followSupport,
  planAppviews,
  profileKind,
} from './discovery.js';
import { assetUrl } from './paths.js';
import { describeAccount, firstRecord, listRecordsCapped } from './xrpc.js';

let mapPromise = null;

export function loadAppviewMap() {
  if (!mapPromise) {
    mapPromise = fetch(assetUrl('appview-map.json')).then((res) => {
      if (!res.ok) throw new Error(`Could not load the appview map (status ${res.status}).`);

      return res.json();
    });
    mapPromise.catch(() => {
      mapPromise = null;
    });
  }

  return mapPromise;
}

/** Bounded-concurrency map over items. */
async function mapLimit(items, limit, fn) {
  const out = Array.from({ length: items.length });
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        const i = cursor++;
        out[i] = await fn(items[i], i);
      }
    }),
  );

  return out;
}

/**
 * Which of `nsids` already hold a record pointing at `targetDid` in the
 * signed-in account's repo. Returns { nsid: { did: {uri, rkey} } }.
 */
async function loadExistingFollows({ forDid, pds, nsids, targetDid }) {
  const result = {};

  const found = await mapLimit(nsids, 4, async (nsid) => {
    try {
      const { records } = await listRecordsCapped(pds, forDid, nsid, 1000);
      const hit = records.find((r) => r.value?.subject === targetDid);

      return hit ? [nsid, { uri: hit.uri, rkey: hit.uri.split('/').pop() }] : null;
    } catch {
      // A missing or unreadable collection simply means "not followed there".
      return null;
    }
  });

  for (const entry of found) {
    if (!entry) continue;
    const [nsid, value] = entry;
    result[nsid] = { [targetDid]: value };
  }

  return result;
}

/**
 * Scan an account.
 * @param {object} args
 * @param {object} args.me  { did, pds } for the signed-in account
 * @param {string} args.target  handle or DID to inspect
 */
export async function scanAccount({ me, target, appviewMap: providedMap = null }) {
  const [appviewMap, account] = await Promise.all([
    providedMap ? Promise.resolve(providedMap) : loadAppviewMap(),
    describeAccount(target),
  ]);

  const index = buildIndex(appviewMap);

  // Profile records: one read per profile-shaped collection, bounded.
  const profileNsids = account.collections.filter((nsid) => profileKind(nsid));

  const records = await mapLimit(profileNsids, 5, async (nsid) => {
    try {
      return [nsid, await firstRecord(account.pds, account.did, nsid)];
    } catch {
      return [nsid, null];
    }
  });

  const profileRecords = Object.fromEntries(records);

  const targetAuthorities = new Set(account.collections.map(authorityOf));

  const followNsids = [...targetAuthorities].flatMap((authority) => {
    const appview = index.get(authority);

    return appview && followSupport(appview).supported ? [appview.follow.nsid] : [];
  });

  const followedByNsid = me
    ? await loadExistingFollows({
        forDid: me.did,
        pds: me.pds,
        nsids: followNsids,
        targetDid: account.did,
      })
    : {};

  const rows = planAppviews({
    collections: account.collections,
    index,
    followedByNsid,
    targetDid: account.did,
  }).map((row) => ({
    ...row,
    profileRecord: row.profile ? profileRecords[row.profile.nsid] || null : null,
  }));

  // Parse at the I/O boundary: a record value's displayName is a string by
  // lexicon, but hostile/broken records may carry anything — coerce once here.
  const asDisplayName = (value) => value?.trim?.() ?? '';

  const displayName =
    rows.map((r) => asDisplayName(r.profileRecord?.value?.displayName)).find((name) => name) || null;

  return {
    account,
    rows,
    displayName,
    collectionCount: account.collections.length,
    mapMeta: {
      generatedAt: appviewMap.generatedAt,
      library: appviewMap.library,
      authorityCount: appviewMap.authorityCount,
    },
  };
}

export async function followOnAppview({ agent, repo, row, targetDid }) {
  if (!row.supported || !row.followNsid) {
    throw new Error(row.blockedReason || 'This appview has no writable follow record type.');
  }

  const record = buildFollowRecord({ nsid: row.followNsid, subjectDid: targetDid });

  const res = await agent.com.atproto.repo.createRecord({
    repo,
    collection: row.followNsid,
    record,
  });

  return { uri: res.data.uri, rkey: res.data.uri.split('/').pop() };
}

export async function unfollowOnAppview({ agent, repo, row }) {
  const rkey = row.existing?.rkey;

  if (!rkey) throw new Error('There is no follow record to remove.');
  await agent.com.atproto.repo.deleteRecord({
    repo,
    collection: row.followNsid,
    rkey,
  });
}
